import { type PaymentMethodType, PaymentsForCaja } from '@seek/payments-widget';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { SITE } from 'src/app/shared/constants/configObjectProperties';
import { SITE_DEFAULT } from 'src/app/shared/constants/site';
import { getConfig } from 'src/app/utils/config';
import { getEnvironment } from 'src/app/utils/environment';
import { useGenerateJobIdempotencyKey } from 'src/app/v1/views/hooks/useGenerateJobIdempotencyKey';
import { usePaymentsWidgetNation } from 'src/app/v1/views/hooks/usePaymentsWidgetNation';
import { PreviewButton } from 'src/app/v1/views/shared/PreviewButton';
import type { AppState } from 'src/types/appState';

import translations from '../.vocab';

interface Props {
  onPaymentSuccess: () => void;
  onPaymentFailed: (error: Error) => void;
  onPostAdButtonClicked: ({
    paymentMethod,
    isSavedCreditCard,
  }: {
    paymentMethod: PaymentMethodType;
    isSavedCreditCard?: boolean;
  }) => Promise<void>;
  handledCancelPayment: () => void;
  onPostAdButtonClickFailed: (error: Error) => void;
}

const paymentSectionStateSelector = (state: AppState) => ({
  adBudgetId: state.session.adBudget?.id,
  billToAgent: state.session.billToAgent,
  currency: state.session.productCurrency,
  interactionId: state.session.interactionId,
  jobOrderId: state.session.jobOrderId,
  seekAdvertiserId: state.session.seekAdvertiserId,
});

export const PaymentsSection = ({
  onPaymentFailed,
  onPaymentSuccess,
  onPostAdButtonClicked,
  handledCancelPayment,
  onPostAdButtonClickFailed,
}: Props) => {
  const { adBudgetId, billToAgent, currency, interactionId, jobOrderId } =
    useSelector(paymentSectionStateSelector, shallowEqual);

  const { t } = useTranslations(translations);

  const idempotencyKey = useGenerateJobIdempotencyKey();
  const paymentWidgetNation = usePaymentsWidgetNation();

  if (!currency) return null;

  return (
    <Stack space="none" data={{ testid: 'apac-payments-widget' }}>
      <PaymentsForCaja
        advertiserNation={paymentWidgetNation}
        billToAgent={billToAgent}
        currency={currency}
        invoiceOnly={true}
        idempotencyKey={idempotencyKey}
        interactionId={interactionId}
        isAdBudgetSelected={Boolean(adBudgetId)}
        handledCancelPayment={handledCancelPayment}
        env={getEnvironment()}
        onPostAdButtonClicked={onPostAdButtonClicked}
        onPostAdButtonClickFailed={onPostAdButtonClickFailed}
        onSuccess={onPaymentSuccess}
        onError={onPaymentFailed}
        orderId={jobOrderId || ''}
        paymentMethod="Invoice"
        previewButtonComponent={<PreviewButton />}
        postAdButtonText={t('refresh-page-button-label')}
        site={getConfig(SITE, SITE_DEFAULT)}
        useCase="CAJA"
      />
    </Stack>
  );
};
